import React from 'react';
import { APITypes } from '../../../../primary-record-types';
import { getLogAvatarProps } from '../utils/getLogAvatarProps';
import LogCardAvatar from './LogCardAvatar';
import Text from '../../../v2BaseComponents/Text';
import { getIntl } from '../../../../utils/intl';
import BaseLogCard from './BaseLogCard';

const intl = getIntl('screens.mainApp.homebase.homebaseScreen.logList.LogCard', {
    viewComments: 'View {count} Comment{plural}',
    showMore: '...show more',
    showLess: 'show less',
    onBehalfOf: `(on behalf of {organization})`,
});

export type LogCardOwnProps = {
    log: APITypes.auditLogService.AuditLogListItem;
    onPress: () => void;
    flat?: boolean;
};

export type LogCardProps = LogCardOwnProps;

type ContentSection = {
    text: string;
    bold: boolean;
};

const splitContent = (content: string, subjectName: string, orgName: string | null): ContentSection[] => {
    const sections: ContentSection[] = [];
    const maybeAddOnBehalfOf = (subjectText: string) => {
        if (!orgName) return subjectText;
        return `${subjectText} ${intl('onBehalfOf', { organization: orgName })}`;
    };

    const subjectText = content.indexOf(`${subjectName}'s`) === 0 ? `${subjectName}'s` : subjectName;
    const subjectIndex = content.indexOf(subjectText);
    if (subjectIndex !== 0) {
        sections.push({ text: content, bold: false });
        return sections;
    }
    sections.push({ text: content.slice(0, subjectIndex), bold: false });
    sections.push({ text: maybeAddOnBehalfOf(subjectText), bold: true });
    sections.push({ text: content.slice(subjectIndex + subjectText.length), bold: false });
    return sections.filter((section) => section.text.length > 0);
};

const LogCard: React.FC<LogCardProps> = (props) => {
    const avatarProps = getLogAvatarProps(props.log);
    const logMessage = props.log.auditLogTitle;
    const messageContent = props.log.type === 'profile-message' ? props.log.references.message : null;

    return (
        <BaseLogCard
            avatar={avatarProps ? <LogCardAvatar {...avatarProps} /> : null}
            headerText={
                avatarProps ? (
                    <Text variant="body-1" color="Dark-Text">
                        {splitContent(
                            logMessage,
                            avatarProps.name,
                            avatarProps.subject === 'user' ? avatarProps.organizationName || null : null,
                        ).map((section, index) => (
                            <Text
                                key={`${index} ${section.text}`}
                                variant={section.bold ? 'bold-body-1' : 'body-1'}
                                color="Dark-Text"
                            >
                                {section.text}
                            </Text>
                        ))}
                    </Text>
                ) : (
                    <Text variant="body-1" color="Dark-Text">
                        {logMessage}
                    </Text>
                )
            }
            logMessage={messageContent}
            timeStamp={new Date(props.log.timestamp).toISOString()}
            commentsCount={props.log.comments.length}
            onPress={props.onPress}
            shouldEnableShowMore={!!messageContent}
            testID={`log-card-${props.log.type}`}
            flat={props.flat}
        />
    );
};

export default React.memo(LogCard);
